import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { Button } from '@mui/material';

import type { ActionItemSelection } from 'src/types/myFollowUp';
import { useFollowUpActionListQuery } from 'src/components/myFollowUp/FollowUpActionPage/hooks/useFollowUpActionListQuery';
import useActionItemPagination from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/useActionItemPagination';
import { useActionItemListQuery } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/hooks';

import { styles } from './index.styles';

interface ActionItemNavigationButtonsProps {
  onRowSelect: (selectedRow: ActionItemSelection) => void;
  currentActionItemId?: number | null;
  setOffset: (offset: number) => void;
  actionItems: any;
  offset: number;
  pageSize: number;
  ordering: string;
  useActionContext?: boolean;
}

const ActionItemNavigationButtons: React.FC<
  ActionItemNavigationButtonsProps
> = ({
  onRowSelect,
  currentActionItemId,
  setOffset,
  actionItems,
  offset,
  pageSize,
  ordering,
  useActionContext = false,
}) => {
  const { t } = useTranslation();

  const queryHook = useActionContext
    ? useFollowUpActionListQuery
    : useActionItemListQuery;

  const { previousActionItem, nextActionItem } = useActionItemPagination({
    currentActionItemId,
    actionItems,
    offset,
    pageSize,
    ordering,
    useQuery: queryHook,
  });

  const selectActionItem = useCallback(
    (actionItem: typeof previousActionItem) => {
      if (!actionItem) return;

      if (actionItem.offset !== undefined) {
        setOffset(actionItem.offset);
      }

      onRowSelect({
        surveyId: actionItem.surveyId || null,
        actionId: actionItem.id,
        type: actionItem.type,
      });
    },
    [setOffset, onRowSelect],
  );

  const selectPreviousActionItem = useCallback(() => {
    selectActionItem(previousActionItem);
  }, [selectActionItem, previousActionItem]);

  const selectNextActionItem = useCallback(() => {
    selectActionItem(nextActionItem);
  }, [selectActionItem, nextActionItem]);
  return (
    <>
      <Button
        startIcon={<NavigateBeforeRoundedIcon />}
        sx={styles.button}
        onClick={selectPreviousActionItem}
        disabled={!previousActionItem}
      >
        {t('prev')}
      </Button>
      <Button
        endIcon={<NavigateNextRoundedIcon />}
        sx={styles.button}
        onClick={selectNextActionItem}
        disabled={!nextActionItem}
      >
        {t('next')}
      </Button>
    </>
  );
};

export default ActionItemNavigationButtons;
